import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  Sparkles, Search, Tag as TagIcon, PlayCircle, ArrowLeft, Calendar,
  Image as ImageIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_) { return ''; }
};

// Turn a URL into a proper embed <iframe src>. Supports YouTube/Vimeo/Cloudinary.
const videoEmbedSrc = (v) => {
  const url = v.url || '';
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return `https://player.vimeo.com/video/${vim[1]}`;
  return null;
};

const InsightCard = ({ insight, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(insight)}
    className="group text-left bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-emerald-300 hover:shadow-md transition-all"
    data-testid={`insight-card-${insight.id}`}
  >
    {insight.cover_image_url ? (
      <div className="aspect-[1200/628] bg-slate-100 overflow-hidden">
        <img src={insight.cover_image_url} alt={insight.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
    ) : (
      <div className="aspect-[1200/628] bg-gradient-to-br from-emerald-100 to-emerald-200 flex items-center justify-center">
        <Sparkles className="h-10 w-10 text-emerald-600 opacity-40" />
      </div>
    )}
    <div className="p-4">
      <div className="text-lg font-semibold text-slate-900 leading-tight line-clamp-2 group-hover:text-emerald-700 transition-colors">
        {insight.title}
      </div>
      {insight.subtitle && (
        <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{insight.subtitle}</div>
      )}
      <div className="flex items-center gap-2 mt-3 text-[11px] text-slate-500">
        <Calendar className="h-3 w-3" />
        {formatDate(insight.published_at)}
        {(insight.videos || []).length > 0 && (
          <>
            <span>•</span>
            <PlayCircle className="h-3 w-3 text-emerald-600" />
            <span>{insight.videos.length} vídeo{insight.videos.length > 1 ? 's' : ''}</span>
          </>
        )}
      </div>
      {(insight.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {insight.tags.slice(0, 3).map((t) => (
            <Badge key={t} variant="outline" className="text-[9px] gap-0.5">
              <TagIcon className="h-2.5 w-2.5" />{t}
            </Badge>
          ))}
        </div>
      )}
    </div>
  </button>
);

const InsightReader = ({ insightId, open, onClose }) => {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !insightId) return;
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/insights/${insightId}`, { withCredentials: true })
      .then((r) => setInsight(r.data))
      .catch(() => toast.error('Não foi possível abrir o insight'))
      .finally(() => setLoading(false));
  }, [open, insightId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0" data-testid="insight-reader-dialog">
        <VisuallyHidden>
          <DialogTitle>{insight?.title || 'Insight'}</DialogTitle>
        </VisuallyHidden>
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Carregando…</div>
        ) : insight && (
          <>
            {insight.cover_image_url && (
              <img src={insight.cover_image_url} alt="Capa"
                className="w-full aspect-[1200/628] object-cover" />
            )}
            <div className="p-6 space-y-4">
              <DialogHeader>
                <DialogTitle className="text-2xl font-black leading-tight">{insight.title}</DialogTitle>
              </DialogHeader>
              {insight.subtitle && (
                <p className="text-base text-slate-600 leading-relaxed">{insight.subtitle}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 border-t border-b border-slate-100 py-2">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDate(insight.published_at)}</span>
                {insight.author_name && <span>por {insight.author_name}</span>}
                {(insight.tags || []).map((t) => (
                  <Badge key={t} variant="outline" className="text-[9px]"><TagIcon className="h-2.5 w-2.5 mr-0.5" />{t}</Badge>
                ))}
              </div>

              {/* Rich body */}
              <article
                className="prose prose-sm max-w-none prose-headings:font-outfit prose-headings:font-bold prose-a:text-emerald-700 prose-img:rounded-lg"
                dangerouslySetInnerHTML={{ __html: insight.body_html || '' }}
              />

              {/* Videos */}
              {(insight.videos || []).length > 0 && (
                <div className="space-y-4 pt-2 border-t border-slate-100">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Vídeos</div>
                  {insight.videos.map((v, i) => {
                    const embed = videoEmbedSrc(v);
                    return (
                      <div key={i} className="rounded-lg overflow-hidden border border-slate-200 bg-black">
                        {v.title && <div className="px-3 py-1.5 bg-slate-900 text-white text-xs">{v.title}</div>}
                        {embed ? (
                          <div className="aspect-video">
                            <iframe
                              src={embed} title={v.title || `Vídeo ${i + 1}`}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              className="w-full h-full"
                            />
                          </div>
                        ) : v.provider === 'cloudinary' ? (
                          <video controls className="w-full aspect-video bg-black" src={v.url} />
                        ) : (
                          <div className="p-4 text-center">
                            <a href={v.url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline text-sm">
                              Abrir vídeo em nova aba →
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const InsightsPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [tag, setTag] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/insights`, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error('Módulo de Insights não habilitado. Contate o suporte.');
        } else {
          toast.error('Falha ao carregar insights');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((i) => (i.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((i) => {
      if (tag && !(i.tags || []).includes(tag)) return false;
      if (ql) {
        const hay = `${i.title} ${i.subtitle || ''} ${(i.tags || []).join(' ')}`.toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [items, q, tag]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="font-outfit text-4xl font-black tracking-tight text-slate-900 flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-emerald-600" />
            Insights
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Conteúdos curados pela equipe MeAdd para acelerar sua campanha.
          </p>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="py-3 px-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar por título, tag…"
                  className="h-9 pl-8 text-sm"
                  data-testid="insights-search-input"
                />
              </div>
              {allTags.length > 0 && (
                <div className="flex flex-wrap gap-1 items-center">
                  <button
                    onClick={() => setTag('')}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${!tag ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:bg-slate-100'}`}
                  >
                    Todos
                  </button>
                  {allTags.map((t) => (
                    <button key={t} onClick={() => setTag(t === tag ? '' : t)}
                      className={`text-[10px] px-2 py-0.5 rounded-full border ${t === tag ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200 hover:bg-slate-100'}`}
                      data-testid={`insight-tag-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
              {(q || tag) && (
                <span className="text-xs text-slate-500">
                  {filtered.length} de {items.length}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Grid */}
        {loading ? (
          <div className="py-16 text-center text-sm text-slate-400">Carregando…</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-slate-200 rounded-xl">
            <ImageIcon className="h-12 w-12 mx-auto text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Nada por aqui ainda.</p>
            {(q || tag) && (
              <Button variant="ghost" size="sm" onClick={() => { setQ(''); setTag(''); }} className="mt-2 text-xs">
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="insights-grid">
            {filtered.map((i) => (
              <InsightCard key={i.id} insight={i} onOpen={(ins) => setSelectedId(ins.id)} />
            ))}
          </div>
        )}
      </div>

      <InsightReader
        insightId={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </Layout>
  );
};

export default InsightsPage;
